import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Divider,
  useTheme,
  Slide,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/**
 * Spec-driven drawer for any direct-API vendor (Horilla today; future
 * ManageEngine, OrangeHRM, ...). Reads a spec from directApiSpecs.js and
 * renders accordingly.
 *
 * Each spec declares one or more `authModes`. The drawer renders a mode
 * selector (hidden when there's only one mode) and swaps the field set.
 * The submitted payload is a JSON object: { auth_mode, ...fieldValues },
 * which the regodit-api DIRECT_API branch parses per vendor + mode.
 *
 * Supported input types are deliberately small: text, url, password.
 * Fancier inputs (file upload, dynamic lists) are added explicitly, not
 * through spec-driven magic.
 */

const CodeSnippet = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: "#f8fafc",
        color: "#0f172a",
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        p: 2,
        pr: 6,
        mb: 3,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: "0.8125rem",
        lineHeight: 1.6,
        overflowX: "auto",
      }}
    >
      <IconButton
        size="small"
        onClick={handleCopy}
        sx={{
          position: "absolute",
          top: 6,
          right: 6,
          color: copied ? "#16a34a" : "#64748b",
          "&:hover": { color: "#0f172a", backgroundColor: "rgba(0,0,0,0.04)" },
        }}
      >
        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
      <Box component="pre" sx={{ m: 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
        {code}
      </Box>
    </Box>
  );
};

const INPUT_TYPE_BY_SPEC = {
  text: "text",
  url: "url",
  password: "password",
};

const emptyValuesFor = (fields) =>
  (fields || []).reduce((acc, field) => {
    acc[field.name] = "";
    return acc;
  }, {});

const DirectApiConnectionDrawer = ({
  open,
  onClose,
  spec,
  integrationKey,
  onStoreIntegrationDetails,
  onReconnect,
  reconnectRciId,
  reconnectBaseUrl,
}) => {
  const theme = useTheme();

  // Normalise: spec may use top-level `fields` (single-mode) or `authModes`
  // (multi-mode). Internally everything is modes[] so the UI is one path.
  const modes = useMemo(() => {
    if (spec?.authModes?.length) return spec.authModes;
    if (spec?.fields?.length) {
      return [
        {
          key: "default",
          label: null,
          description: null,
          fields: spec.fields,
        },
      ];
    }
    return [];
  }, [spec]);

  const [selectedModeKey, setSelectedModeKey] = useState(modes[0]?.key || "");
  const selectedMode = useMemo(
    () => modes.find((m) => m.key === selectedModeKey) || modes[0] || null,
    [modes, selectedModeKey]
  );
  const [valuesByMode, setValuesByMode] = useState(() =>
    modes.reduce((acc, mode) => {
      acc[mode.key] = emptyValuesFor(mode.fields);
      return acc;
    }, {})
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Keep selectedModeKey valid if spec changes mid-render (prop update).
    if (modes.length && !modes.some((m) => m.key === selectedModeKey)) {
      setSelectedModeKey(modes[0].key);
    }
  }, [modes, selectedModeKey]);

  useEffect(() => {
    if (!open || !reconnectBaseUrl) return;
    setValuesByMode((prev) => {
      const next = { ...prev };
      for (const mode of modes) {
        if ((mode.fields || []).some((f) => f.name === "base_url")) {
          next[mode.key] = { ...(next[mode.key] || {}), base_url: reconnectBaseUrl };
        }
      }
      return next;
    });
  }, [open, reconnectBaseUrl, modes]);

  const slideTransition = useMemo(
    () => (props) => <Slide {...props} direction="left" />,
    []
  );

  const values = valuesByMode[selectedModeKey] || {};
  const trimmed = useMemo(() => {
    const result = {};
    for (const [name, value] of Object.entries(values)) {
      result[name] = typeof value === "string" ? value.trim() : value;
    }
    return result;
  }, [values]);

  const missingRequired = useMemo(
    () =>
      (selectedMode?.fields || []).some(
        (field) => field.required && !trimmed[field.name]
      ),
    [selectedMode, trimmed]
  );

  const canSubmit = selectedMode && !missingRequired && !submitting;

  const handleFieldChange = (name) => (event) => {
    const next = { ...values, [name]: event.target.value };
    setValuesByMode((prev) => ({ ...prev, [selectedModeKey]: next }));
  };

  const handleModeChange = (_event, newMode) => {
    if (!newMode) return;
    setSelectedModeKey(newMode);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = { auth_mode: selectedModeKey, ...trimmed };
      let result;
      if (reconnectRciId && onReconnect) {
        result = await onReconnect({
          rciId: reconnectRciId,
          payload: JSON.stringify(payload),
        });
      } else {
        if (!onStoreIntegrationDetails) {
          console.error("onStoreIntegrationDetails is not provided");
          return;
        }
        result = await onStoreIntegrationDetails({
          integratedAccountId: JSON.stringify(payload),
          integrationKey,
          integrationType: "DIRECT_API",
          sync: true,
        });
      }
      if (result?.success) {
        onClose();
      }
    } catch (error) {
      console.error("Error updating integration details:", error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!spec || !selectedMode) {
    return null;
  }

  const showModeSelector = modes.length > 1;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      TransitionComponent={slideTransition}
      PaperProps={{
        sx: {
          width: "60%",
          maxWidth: "900px",
          backgroundColor: theme.customColors.background.main,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#FFFFFF",
          borderBottom: `1px solid ${theme.customColors.border.subtle}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 4,
            py: 3,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme.customColors.text.strong,
                letterSpacing: "-0.02em",
              }}
            >
              {reconnectRciId
                ? `Reconnect ${spec.label}`
                : spec.heading || `Connect ${spec.label}`}
            </Typography>
            {spec.subtitle && (
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.secondary,
                  mt: 0.5,
                }}
              >
                {spec.subtitle}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ color: theme.customColors.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 4,
          py: 3,
        }}
      >
        {spec.alert && (
          <Alert severity={spec.alert.severity || "info"} sx={{ mb: 3 }}>
            {spec.alert.message}
          </Alert>
        )}


        {(selectedMode.fields || []).map((field, index) => (
          <React.Fragment key={`${selectedModeKey}-${field.name}`}>
            {index > 0 && <Divider sx={{ my: 3 }} />}
            <TextField
              label={field.label}
              value={values[field.name] || ""}
              onChange={handleFieldChange(field.name)}
              type={INPUT_TYPE_BY_SPEC[field.type] || "text"}
              fullWidth
              placeholder={field.placeholder}
              required={field.required}
              helperText={
                field.name === "base_url" && spec?.baseUrlDocs ? (
                  <>
                    {field.helper}{" "}
                    <Link
                      href={spec.baseUrlDocs.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      {spec.baseUrlDocs.label}
                    </Link>
                  </>
                ) : (
                  field.helper
                )
              }
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
              }}
            />
            {field.name === "base_url" && selectedMode.snippet && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.customColors.text.secondary,
                    mb: 1,
                  }}
                >
                  Run this against your Horilla portal to mint the token pair:
                </Typography>
                <CodeSnippet code={selectedMode.snippet(values)} />
              </Box>
            )}
            {field.name === "base_url" && showModeSelector && (
              <Box sx={{ mt: 4 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    mb: 1.5,
                    color: theme.customColors.text.strong,
                  }}
                >
                  Which token do you want to use?
                </Typography>
                <RadioGroup
                  value={selectedModeKey}
                  onChange={(e) => setSelectedModeKey(e.target.value)}
                  sx={{ gap: 1.5 }}
                >
                  {modes.map((mode) => {
                    const isSelected = selectedModeKey === mode.key;
                    return (
                      <Box
                        key={mode.key}
                        onClick={() => setSelectedModeKey(mode.key)}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                          p: 2,
                          borderRadius: "10px",
                          border: `1px solid ${
                            isSelected
                              ? theme.palette.primary.main
                              : theme.customColors.border.subtle
                          }`,
                          backgroundColor: isSelected
                            ? "rgba(3, 27, 137, 0.03)"
                            : "transparent",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: isSelected
                              ? "rgba(3, 27, 137, 0.05)"
                              : "rgba(0, 0, 0, 0.02)",
                          },
                        }}
                      >
                        <Radio
                          checked={isSelected}
                          value={mode.key}
                          size="small"
                          sx={{ p: 0, mt: 0.25 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: theme.customColors.text.strong,
                              mb: 0.25,
                            }}
                          >
                            {mode.label || mode.key}
                          </Typography>
                          {mode.description && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.customColors.text.secondary,
                                display: "block",
                                lineHeight: 1.5,
                              }}
                            >
                              {mode.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </RadioGroup>
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "#FFFFFF",
          borderTop: `1px solid ${theme.customColors.border.subtle}`,
          px: 4,
          py: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            color: theme.customColors.text.primary,
            fontWeight: 500,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={<CheckCircleIcon />}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: "white",
            textTransform: "none",
            px: 4,
            py: 1.5,
            fontWeight: 600,
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: theme.customColors.button.hover,
            },
            "&:disabled": {
              backgroundColor: theme.customColors.background.disabled,
              color: theme.customColors.text.disabled,
            },
          }}
        >
          {submitting
            ? reconnectRciId
              ? "Reconnecting..."
              : "Connecting..."
            : reconnectRciId
            ? "Reconnect"
            : "Connect Integration"}
        </Button>
      </Box>
    </Drawer>
  );
};

export default DirectApiConnectionDrawer;
